import type { CSSInterpolation } from '@emotion/serialize';
import React, { forwardRef, use } from 'react';

import DownloadIcon from 'src/components/icons/download';
import Preview from 'src/components/icons/preview';
import RightArrowIcon from 'src/components/icons/right-arrow-icon';
import Search from 'src/components/icons/search-icon';
import { ClubCmsContent } from 'src/content/club-content-consumer';
import { shouldUseClubRedesignForClub } from 'src/content/club-look-ups-by-id';
import { SiteContext } from 'src/context/site-context';
import type { Styling } from 'src/context/styling';
import { RedesignButtonIcon } from 'src/enums/button-icons';
import type { ButtonStyle, RedesignButtonStyle } from 'src/enums/button-style';

import type { GeneralRedesignButtonStyle } from '../../../cms/common-widgets/button-widget';

import { getBuildButtonCss, getNewButtonCss } from './buttonCss';
import ClubLinkButtonContent from './club-link-button-content';
import type { ButtonDetails } from './club-link-button-content';
import GlobalLinkButtonContent from './global-link-button-content';

export const getButtonIcon = (icon: string | undefined | null, style?: React.CSSProperties) => {
  switch (icon) {
    case RedesignButtonIcon.DOWNLOAD:
      return <DownloadIcon svgStyle={{ height: '1rem', ...style }} />;
    case RedesignButtonIcon.EYE:
      return <Preview svgStyle={{ height: '1rem', ...style }} />;
    case RedesignButtonIcon.ARROW:
      return <RightArrowIcon svgStyle={{ height: '1rem', ...style }} />;
    case RedesignButtonIcon.SEARCH:
      return <Search svgStyle={{ height: '1rem', ...style }} />;
    default:
      return undefined;
  }
};

export type LinkButtonProps = ButtonDetails & {
  goToGlobal?: boolean | null;
  customButtonStyle?: CSSInterpolation;
  className?: string;
  contentClassName?: string;
  iconView?: string | null;
  isRedesignButton?: boolean;
  handleClick?: () => void;
};

export const renderLinkButton = ({
  button,
  styling,
  handleClick,
  cssStyle,
  className = '',
  openInNewTab = false,
  gtmTrackingId = '',
  buttonType = 'link-button',
  buttonStyle,
  customButtonStyle = [],
}: {
  button: Queries.RedesignButton;
  styling: Styling;
  handleClick?: (button: Queries.RedesignButton) => void;
  cssStyle?: CSSInterpolation;
  customButtonStyle?: CSSInterpolation[];
  className?: string;
  gtmTrackingId?: string;
  buttonType?: string;
  openInNewTab?: boolean;
  buttonStyle?: GeneralRedesignButtonStyle;
}) => {
  const linkButton = (
    <LinkButton
      buttonText={button.buttonText}
      buttonLink={button.buttonLink}
      buttonStyle={(buttonStyle || button.buttonStyle) as ButtonStyle}
      buttonType={buttonType}
      gtmTrackingId={gtmTrackingId}
      customButtonStyle={[
        getNewButtonCss((buttonStyle || button.buttonStyle) as RedesignButtonStyle, styling),
        ...customButtonStyle,
      ]}
      iconView={button.buttonIcon}
      handleClick={() => handleClick?.(button)}
      goToGlobal={!!button.goToGlobal}
      openInNewTab={openInNewTab}
      forceOpenInSameTab={!!button.forceOpenInSameTab}
      className={className}
      isRedesignButton
    />
  );
  return cssStyle ? <div css={cssStyle}>{linkButton}</div> : linkButton;
};

const LinkButton = forwardRef<HTMLElement, LinkButtonProps>((props, ref) => {
  const { styling, isGlobalPage, siteId, buildType } = use(SiteContext);
  const cmsData = use(ClubCmsContent);

  const buttonStyle =
    props?.customButtonStyle || getBuildButtonCss(props.buttonStyle, styling, buildType);
  const buttonEndIcon = props.buttonEndIcon || getButtonIcon(props.iconView);

  return isGlobalPage || props.goToGlobal ? (
    <GlobalLinkButtonContent
      {...props}
      buttonCss={buttonStyle}
      buttonEndIcon={buttonEndIcon}
      ref={ref}
    />
  ) : (
    <ClubLinkButtonContent
      {...props}
      buttonCss={buttonStyle}
      features={shouldUseClubRedesignForClub(siteId) ? cmsData?.newFeatures : cmsData?.features}
      enabledPages={cmsData?.pages}
      buttonEndIcon={buttonEndIcon}
      ref={ref}
    />
  );
});

LinkButton.displayName = 'LinkButton';

export default LinkButton;
